/**
 * @author Junaid Atari <mj.atari@gmail.com>
 * @see https://github.com/blacksmoke26/dawn-of-man-generator
 * @since 2024-06-23
 * @version 2.4
 */

import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classname';
import merge from 'deepmerge';
import {Row} from 'react-bootstrap';
import {capitalCase} from 'change-case';
import uniqueRandomArray from 'unique-random-array';

// elemental components
import Accordion from '~/components/ui/Accordion';
import ActionHeader from './elements/ActionHeader';
import PropertyLabel from '~/components/ui/PropertyLabel';
import AttributeSelect from '~/components/ui/elements/AttributeSelect';
import PropertyCheckboxLabel from '~/components/ui/PropertyCheckboxLabel';
import AttributeCheckbox from '~/components/ui/elements/AttributeCheckbox';

// hooks
import useValues from '~/hooks/use-values';

// utils
import {actionDefaultProps} from './utils/default';
import {defaultsParams, FEATURE_TYPE} from '~/utils/action';

// parsers
import {filterEmpty} from '~/utils/template';
import {toActionTemplate} from '~/utils/parser/templates-action';

// types
import type {
  ActionAttributesProps, ActionName, ActionProps, ActionSetFeatureEnabled
} from '~/types/action.types';

export interface Props extends ActionProps<ActionSetFeatureEnabled> {
}

export interface SetFeatureEnabledActionAttributesProps extends ActionAttributesProps {
  valueChecked: boolean;
}

const ACTION_NAME: ActionName = 'SetFeatureEnabled';

const SetFeatureEnabled = (props: Props) => {
  const newProps = merge<Required<Props>>(actionDefaultProps, props);

  const valuer = useValues<Partial<ActionSetFeatureEnabled>>(
    merge(defaultsParams?.setFeatureEnabled || {}, props?.initialValues || {}),
  );

  const state = useValues<SetFeatureEnabledActionAttributesProps>({
    disabled: newProps.disabled as boolean,
    disabledCheckbox: newProps.disabledCheckbox as boolean,
    expanded: newProps.expanded as boolean || true,
    valueChecked: !valuer.is('value', undefined),
  });

  // Reflect values changes
  React.useEffect(() => {
    const changeValues = {...valuer.data};

    if (!state.data.valueChecked) {
      changeValues.value = undefined;
    }

    newProps?.onTemplate(toActionTemplate(ACTION_NAME, changeValues, state.data.disabled));
    newProps?.onValuesChange(filterEmpty(changeValues));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.data.disabled, state.data.valueChecked,
    valuer.data,
  ]);

  // Reflect state changes
  React.useEffect(() => {
    newProps.onChange(state.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data]);

  // Reflect prop changes
  React.useEffect(() => {
    state.set('disabled', props?.disabled, true);
    state.set('disabledCheckbox', props?.disabledCheckbox, true);
    state.set('expanded', props?.expanded, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.disabled, props?.disabledCheckbox, props?.expanded]);

  const isDisabled = state.get<boolean>('disabledCheckbox', false) || state.get<boolean>('disabled', false);

  return (
    <div className={cn('mb-3', {'text-muted': isDisabled}, 'checkbox-align')}>
      {newProps?.showHeader && (
        <ActionHeader
          caption={ACTION_NAME}
          disabled={state.data.disabled}
          disabledCheckbox={state.data.disabledCheckbox}
          removeIcon={newProps.removeIcon}
          onRemoveClick={newProps.onRemoveClick}
          onExpandedClick={(current: boolean) => state.set('expanded', current)}
          expanded={state.data.expanded}/>
      )}
      {state.data.expanded && (
        <>
          <Row className="mb-2 mt-2">
            <PropertyLabel caption="Feature"/>
            <AttributeSelect
              className="w-75"
              colProps={{sm: '6'}}
              disabled={isDisabled}
              options={FEATURE_TYPE.map(value => ({label: capitalCase(value), value}))}
              value={valuer.get('feature', null)}
              onSelect={option => valuer.set('feature', option.value)}
              allowShuffle
              onShuffle={() => {
                valuer.set('feature', uniqueRandomArray(FEATURE_TYPE));
              }}
            />
          </Row>
          <Accordion
            noBodyPad={true}
            noCard={true}
            header="Optional parameters"
            eventKey="spawn_optional_parameters">
            <Row className="mt-2">
              <PropertyCheckboxLabel
                caption="Value"
                checked={state.get<boolean>('valueChecked', false)}
                disabled={isDisabled}
                undefinedSetter={[valuer, 'value', false]}
                onChange={isChecked => {
                  state.set('valueChecked', isChecked);
                }}
              />
              <AttributeCheckbox
                checked={valuer.get<boolean>('value', false)}
                caption="Yes"
                disabled={isDisabled || !state.get<boolean>('valueChecked', false)}
                onChange={isChecked => {
                  valuer.set('value', isChecked);
                }}
              />
            </Row>
          </Accordion>
        </>
      )}
    </div>
  );
};

// Properties validation
SetFeatureEnabled.propTypes = {
  disabled: PropTypes.bool,
  disabledCheckbox: PropTypes.bool,
  removeIcon: PropTypes.bool,
  onRemoveClick: PropTypes.func,
  expanded: PropTypes.bool,
  showHeader: PropTypes.bool,
  initialValues: PropTypes.exact({
    feature: PropTypes.oneOf(FEATURE_TYPE),
    value: PropTypes.bool,
  }),
  onChange: PropTypes.func,
  onValuesChange: PropTypes.func,
  onTemplate: PropTypes.func,
};

export default SetFeatureEnabled;
